import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getDeviceFingerprint } from '../utils/device-fingerprint';

/** Represents the non-sensitive user data received after login. */
export interface AuthUser {
  role: string;
  userName: string;
  sessionId: string;
  fullName?: string;
  pictureUrl?: string;
}

/**
 * Authentication service that manages user session state.
 * Tokens are stored in HttpOnly cookies by the backend —
 * this service only tracks non-sensitive session data in memory.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  /** Current authenticated user (null when not logged in). */
  readonly currentUser = signal<AuthUser | null>(null);

  constructor() {
    this.checkInitialState();
  }

  private checkInitialState() {
    if (typeof window === 'undefined') return;
    const userJson = localStorage.getItem('admin_user');
    if (userJson) {
      try {
        this.currentUser.set(JSON.parse(userJson));
      } catch (e) {
        localStorage.removeItem('admin_user');
      }
    }
  }

  /** Computed signal indicating if a user is currently authenticated. */
  readonly isLoggedIn = computed(() => this.currentUser() !== null);

  private mapUser(res: any): AuthUser {
    return {
      role: res.role,
      userName: res.userName,
      sessionId: res.sessionId,
      fullName: res.fullName || undefined,
      pictureUrl: res.pictureUrl || undefined
    };
  }

  /**
   * Authenticates the user. The backend sets HttpOnly cookies automatically.
   * Only the non-sensitive JSON payload is stored in memory.
   */
  login(userName: string, password: string, turnstileToken?: string): Observable<AuthUser> {
    return from(getDeviceFingerprint()).pipe(
      switchMap((fingerprint) =>
        this.http.post<any>(`${this.apiUrl}/auth/login`, { userName, password, turnstileToken, deviceFingerprint: fingerprint })
      ),
      tap((res) => {
        const user = this.mapUser(res);
        this.currentUser.set(user);
        localStorage.setItem('admin_user', JSON.stringify(user));
      })
    );
  }

  /**
   * Logs out the user. The backend clears the HttpOnly cookies.
   */
  logout(): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/auth/logout`, {}).pipe(
      tap(() => {
        this.currentUser.set(null);
        localStorage.removeItem('admin_user');
      })
    );
  }

  /**
   * Clears local session state without calling the API.
   * Used by the error interceptor when a 401 is received (token already invalid).
   */
  clearSession(): void {
    this.currentUser.set(null);
    localStorage.removeItem('admin_user');
  }

  /**
   * Refreshes the JWT by sending the existing refresh token cookie.
   * The backend issues new HttpOnly cookies automatically.
   */
  refreshToken(): Observable<AuthUser> {
    return this.http
      .post<any>(`${this.apiUrl}/auth/refresh-token`, {})
      .pipe(
        tap({
          next: (res) => {
            const user = this.mapUser(res);
            this.currentUser.set(user);
            localStorage.setItem('admin_user', JSON.stringify(user));
          },
          error: () => {
            this.currentUser.set(null);
            localStorage.removeItem('admin_user');
          }
        })
      );
  }

  /**
   * Updates the admin's display name and optional avatar.
   */
  updateProfile(fullName: string, avatar?: File): Observable<{ fullName: string; avatarUrl?: string }> {
    const formData = new FormData();
    formData.append('fullName', fullName);
    if (avatar) {
      formData.append('avatar', avatar);
    }
    return this.http.put<{ fullName: string; avatarUrl?: string }>(`${this.apiUrl}/auth/admin-profile`, formData).pipe(
      tap((res) => {
        const current = this.currentUser();
        if (current) {
          const updated: AuthUser = {
            ...current,
            fullName: res.fullName,
            pictureUrl: res.avatarUrl || current.pictureUrl
          };
          this.currentUser.set(updated);
          localStorage.setItem('admin_user', JSON.stringify(updated));
        }
      })
    );
  }
}
